/* file description */

import React, {
  useRef,
  forwardRef,
  useImperativeHandle,
  ForwardRefExoticComponent,
  PropsWithoutRef,
  RefAttributes,
  useMemo,
  useEffect,
} from 'react';
import { ActionType } from '@cui/pro-components';
import { EditableTable } from './components/EditableTable.tsx';
import { UploadFile } from './effect/component/UploadFile';
import { TableImport } from './effect/component/TableImport';
import { Preview } from './effect/component/Preview';
import { Export } from './effect/component/Export';
import {
  useColumnsProp,
  useModule,
  useRequestProp,
  useScrollProp,
  useToolBarRender,
  useUploadWrap,
  useOptionColumns,
  useStore,
  useFooter,
} from './effect';
import type { SPTableWithModelProps } from './type';
import zhCN from 'antd/es/locale/zh_CN';
import { Button, ConfigProvider, Alert } from 'antd';
import { isMobileEnv, getExpressionsValue, Api as FetchAPI } from '@/utils';
import { MobileTable } from './components/MobileTable';
import useMetaStore from '@/store/useMetaStore';

export const SPTableWithModel: ForwardRefExoticComponent<
  PropsWithoutRef<SPTableWithModelProps> & RefAttributes<Record<any, any>>
> = forwardRef<Record<any, any>, SPTableWithModelProps>(
  (
    {
      key,
      taskId,
      namespaceID,
      moduleID,
      pageType,
      assetModuleInfo,
      columns,
      // request,
      rowKey,
      // autoRowEditConfig,
      // autoAddRecordConfig,
      scroll,
      headerTitle,
      filter,
      onChange,
      // extraParams,
      fileConfig,
      canImport,
      toolBarRender,
      headBtnsList,
      optionColumns,
      // editable,
      requestParams,
      value,
      rowCopyable = false,
      rowDelable = true,
      dataVersion = '0',
      openVersion,
      tableBaseMode = 'view', // view 发起分页请求 | edit 发起全量数据请求 | add 不发起请求
      onSearch,
      searchParams,
      search = true,
      onRow, // function(record, index)
      rowSelection,
      showTableIndex,
      isNotMobile,
      storeValue,
      store,
      defaultPagination,
      hidePagination,
      sorter,
      headerBtns,
      selectedRows,
      title,
      footerExpression,
      onValidateChange,
      floadSearch, // 移动端区分搜索框展现形式
      mobileRows = [], // 移动端展示的行
      isMobile,
    },
    ref,
  ) => {
    // props taskId和pageType可能为空，此处自己从useMetaStore中取，保障不为空
    const { taskId: taskid, pageType: pagetype } = useMetaStore();
    const actionRef = useRef<ActionType>();
    const tableBaseRef = useRef<any>();
    const editableRef = useRef<any>();
    // 请求模型信息
    const { moduleInfo } = useModule({
      FetchAPI,
      namespaceID,
      moduleID,
      assetModuleInfo,
    });
    // 前端行唯一id
    // const rowKey = '_id';
    // 行删除标识
    const isDeleteKey = '_isDelete';
    const { mergeRequest } = useRequestProp({
      tableBaseMode,
      moduleInfo,
      // request,
      FetchAPI,
      dataVersion,
      openVersion,
    });

    const {
      uploadWrapVisible,
      setUploadWrap,
      importVisible,
      setImportVisible,
      previewProps,
      setPreviewProps,
      exportVisible,
      setExportVisible,
    } = useUploadWrap();
    const { mergeScroll } = useScrollProp({ scroll });

    const rowUniqueKey = '_id';

    // 生成搜索项默认值，每当columns 变更时执行
    const defaultSearchParams = useMemo(() => {
      const columsWithSearchOptions = columns.filter((x) => (x.search && x.searchOptions));
      const res: any = {};
      columsWithSearchOptions.forEach((col) => {
        res[col.dataIndex] = getExpressionsValue(col.searchOptions?.defaultValue);
      });
      return res;
    }, [JSON.stringify(columns.map((x) => x.searchOptions))]);

    const {
      rowsData,
      setRowsData,
      setRowData,
      addEditRecord,
      reload,
      reset,
      pagination,
      setParams,
      searchParams: editableSearchParams,
      setTableInfo,
      loading,
      filterSql,
    } = useStore({
      defaultPagination,
      tableBaseMode,
      value,
      request: mergeRequest,
      namespaceID,
      moduleID,
      filter,
      moduleInfo,
      requestParams,
      rowKey: rowUniqueKey,
      onChange, // 需要首次手动触发
      onSearch,
      searchParams: { ...searchParams, ...defaultSearchParams },
      sorter,
    });
    // const { mergeColumns, primaryKey } = useColumns({
    //   columns,
    //   moduleInfo,
    //   rowKey,
    //   fileConfig,
    //   store,
    // });

    const { mergeColumns, primaryKey } = useColumnsProp({
      columns,
      moduleInfo,
      rowKey,
      FetchAPI,
      mode: tableBaseMode, // 透传表格编辑态还是详情态
      fileConfig,
      rowsData,
      setRowData,
      storeValue,
      store,
      isMobile,
    });

    let mergeToolBarRender = useToolBarRender({
      toolBarRender,
      fileModule: fileConfig?.fileModule,
      setUploadWrap,
      canImport,
      setImportVisible,
      moduleID,
      moduleInfo,
      mergeColumns,
      rowsData,
      headerBtns,
      rowSelection,
      selectedRows,
      setExportVisible,
    });

    const mergeRowKey = rowKey || rowUniqueKey;

    if (headBtnsList) {
      mergeToolBarRender = headBtnsList({
        mergeColumns,
        mergeRequest,
        requestParams,
        addEditRecord,
        editableRef,
        canImport,
        mergeRowKey,
        fileConfig,
      });
    }
    const mergeOptionColumns = useOptionColumns({
      fileModule: fileConfig?.fileModule,
      optionColumns,
      rowCopyable,
      rowDelable,
      addEditRecord,
      actionRef,
      editableRef,
      mode: tableBaseMode,
      fileFieldMap: fileConfig?.fileFieldMap,
      rowsData,
      onChange,
      setRowsData,
      setPreviewProps,
      moduleID,
      moduleInfo,
      isMobile,
    });

    useImperativeHandle(ref, () => ({
      // 行数据添加，支持单个对象或者数组
      addEditRecord,
      reset,
      setRowData,
      getRowsData: () => {
        return rowsData;
      },
      filterSql,
      // fix jira SPZN-9229 先关闭，不直接透传实例引用
      tableRef: { ...actionRef?.current, ...tableBaseRef?.current },
      reload,
      validateFields: () => {
        return editableRef.current?.validateFields();
      },
      primaryKey,
      editableRef,
      getSearchParams: () => editableRef.current.getSearchParams(),
      removeRow: (index: number) => editableRef.current.removeRow(index),
      removeRowById: (id: string | string[]) => editableRef.current.removeRowById(id),
    }));

    const valueChange = (data: any) => {
      let newValues = Object.values(data).map((newRow: any) => {
        const RecordValue =
          rowsData.find(
            (row) => row[mergeRowKey] === newRow[mergeRowKey],
          ) || {};
        if (!RecordValue) {
          return newRow;
        }
        return {
          ...RecordValue,
          ...newRow,
        };
      });
      // 修复number失焦时无法删除行数据的问题
      // 目前只有行字段变更或者行删除更时触发，可以执行的脏数据过滤
      newValues = newValues.filter((newRow) => rowsData.find((current) => newRow._id === current._id));
      setRowsData(newValues);
      onChange && onChange(newValues);
    };

    const pga = useMemo(() => {
      if (hidePagination) {
        return false;
      }
      return pagination;
    }, [pagination, hidePagination]);

    const tableValue = rowsData.filter((x) => x[isDeleteKey] !== true);
    const footer = useFooter({
      footerExpression,
      value: { [moduleInfo.handle]: tableValue },
    });
    return (
      <ConfigProvider locale={zhCN}>
        {!isNotMobile && isMobile ? (
          <MobileTable
            key={moduleID}
            notMore={!!value}
            value={rowsData.filter(
              (x) => x[mergeRowKey] && x[isDeleteKey] !== true,
            )}
            ref={editableRef}
            rowKey={mergeRowKey}
            onRow={onRow}
            mergeColumns={mergeColumns}
            search={search}
            setTableInfo={setTableInfo}
            pagination={value ? false : pga}
            editableSearchParams={editableSearchParams}
            setParams={setParams}
            mergeOptionColumns={mergeOptionColumns}
            toolBarRender={mergeToolBarRender}
            tableBaseMode={tableBaseMode}
            onChange={valueChange}
            floadSearch={floadSearch}
            rowSelection={rowSelection}
            title={title}
            mobileRows={mobileRows}
            selectedRows={selectedRows}
          />
        ) : (
          <EditableTable
            key={moduleID}
            ref={editableRef}
            search={search}
            onRow={onRow}
            scroll={mergeScroll}
            rowKey={mergeRowKey}
            toolBarRender={mergeToolBarRender}
            optionColumns={mergeOptionColumns}
            columns={mergeColumns}
            value={tableValue}
            onChange={valueChange}
            pagination={pga}
            tableBaseMode={tableBaseMode}
            onSearch={(params: any) => {
              setParams(params);
            }}
            searchParams={editableSearchParams}
            onTableChange={setTableInfo}
            rowSelection={rowSelection}
            loading={loading}
            title={title}
            footer={footer}
            onValidateChange={onValidateChange}
            showTableIndex={showTableIndex}
          />
        )}

        <UploadFile
          uploadWrapVisible={uploadWrapVisible}
          setUploadWrap={setUploadWrap}
          namespaceID={namespaceID}
          moduleID={moduleID}
          moduleInfo={moduleInfo}
          FetchAPI={FetchAPI}
          reload={reload}
          addEditRecord={(...arg) => {
            addEditRecord(...arg);
            setImmediate(() => {
              editableRef.current?.validateFields();
            });
          }}
          mergeRowKey={mergeRowKey}
          fileConfig={fileConfig}
          isMobile={isMobile}
        />
        <TableImport
          uploadWrapVisible={importVisible}
          setUploadWrap={setImportVisible}
          moduleInfo={moduleInfo}
          columns={mergeColumns}
          addEditRecord={(...arg) => {
            addEditRecord(...arg);
            setImmediate(() => {
              editableRef.current?.validateFields();
            });
          }}
          taskId={taskid}
          pageType={pagetype}
          isMobile={isMobile}
          reload={reload}
        />
        <Preview
          previewProps={previewProps}
          setPreviewProps={setPreviewProps}
        />
        <Export
          exportVisible={exportVisible}
          setExportVisible={setExportVisible}
          moduleInfo={moduleInfo}
          mergeColumns={mergeColumns}
          selectedRows={selectedRows}
          rowSelection={rowSelection}
          request={mergeRequest}
          namespaceID={namespaceID}
          moduleID={moduleID}
          filter={filter}
          requestParams={requestParams}
          searchParams={searchParams}
          sorter={sorter}
        />
      </ConfigProvider>
    );
  },
);

export default SPTableWithModel;
